import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { DateTime } from 'luxon'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import { Box, Grid, InputAdornment } from '@material-ui/core'
import { DatePicker, KeyboardTimePicker } from '@material-ui/pickers'
import EventIcon from '@material-ui/icons/Event'
import Typography from '@material-ui/core/Typography'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import Divider from '@material-ui/core/Divider'
import { useAuth } from 'lib/auth'
import { CoachScheduleAvailability } from './query'
import Dialog from 'components/dialog'
import RouterLinkButton from 'components/router-link-button'
import { useStyles } from './styles'
import ConfirmOverwriteAvailabilityDialog from './confirm-overwrite-availability-dialog'
import CustomLuxonUtils from 'lib/CustomLuxonUtils'
import TimeIcon from '@material-ui/icons/AccessTime'
import isValid from 'date-fns/isValid'

const OverwriteAvailabilityDialog = ({
  trigger,
  currentUrl,
  durationInMinutes,
}) => {
  const { user } = useAuth()
  const classes = useStyles()
  const [startDate, setStartDate] = useState(null)
  const [startTime, setStartTime] = useState(null)

  const startDateTime =
    startDate &&
    startTime &&
    new CustomLuxonUtils().mergeDateAndTime(
      DateTime.fromJSDate(startDate),
      DateTime.fromJSDate(startTime),
    )

  const { data } = useQuery(CoachScheduleAvailability, {
    skip: !startDateTime,
    variables: {
      id: user?.coach?.id,
      timespan: {
        from: startDateTime,
        to: startDateTime?.plus({ minutes: durationInMinutes }),
      },
    },
  })

  const changeStartTime = value => {
    if (!isValid(value)) return
    const minutes = value.getMinutes()
    const roundedMinutes = Math.floor(minutes / 5) * 5
    value.setMinutes(roundedMinutes)

    setStartTime(value)
  }

  const warningMessages = data?.coach.scheduleAvailability.warnings
  const isAvailableTime = !!data?.coach.scheduleAvailability.isAvailable

  return (
    <Dialog trigger={trigger} maxWidth="sm" fullWidth showCloseButton>
      {({ closeDialog }) => {
        return (
          <>
            <DialogTitle>Choose time</DialogTitle>
            <Divider />
            <DialogContent>
              <Grid container>
                <Grid item xs={12} sm={7}>
                  <Box mb={2}>
                    <Typography variant="subtitle1" gutterBottom>
                      Date
                    </Typography>
                    <DatePicker
                      id="start-date"
                      label="Select date"
                      value={startDate}
                      format="MMM dd, yyyy"
                      onChange={setStartDate}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment>
                            <EventIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Typography variant="subtitle1" gutterBottom>
                    Start time
                  </Typography>
                  <Box mb={2}>
                    <KeyboardTimePicker
                      id="start-time"
                      label="Select time"
                      minutesStep={5}
                      mask="__:__ _M"
                      keyboardIcon={<TimeIcon />}
                      value={startTime}
                      onChange={val => changeStartTime(val)}
                      InputProps={{
                        classes: {
                          adornedEnd: classes.adornedEnd,
                        },
                      }}
                    />
                  </Box>
                </Grid>

                {warningMessages?.map(warningMessage => (
                  <Grid
                    key={warningMessage}
                    item
                    container
                    xs={12}
                    className={classes.warnWrapper}
                  >
                    <Grid item container xs={2} sm={1}>
                      <ReportProblemOutlinedIcon />
                    </Grid>
                    <Grid item xs={10}>
                      <Box>
                        <Typography variant="body1" gutterBottom>
                          {warningMessage}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button color="primary" onClick={closeDialog}>
                Cancel
              </Button>
              {warningMessages?.length > 0 ? (
                <ConfirmOverwriteAvailabilityDialog
                  trigger={
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={!startDateTime || !isAvailableTime}
                    >
                      Select
                    </Button>
                  }
                  startDateTime={startDateTime}
                  currentUrl={currentUrl}
                />
              ) : (
                <RouterLinkButton
                  color="primary"
                  variant="contained"
                  disabled={!startDateTime || !isAvailableTime}
                  to={`${currentUrl}&startDateTime=${startDateTime}`}
                >
                  Select
                </RouterLinkButton>
              )}
            </DialogActions>
          </>
        )
      }}
    </Dialog>
  )
}

export default OverwriteAvailabilityDialog
